// store/slices/competitorSlice.js - Updated with processing status tracking (like gapBrandsSlice)
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Get API URL from environment variable
const API_URL = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

// Processing step messages (user-friendly, non-technical)
// Backend has 8 steps (1-8) then 'completed'
const PROCESSING_STEPS = {
  '1': 'Starting analysis...',
  '2': 'Fetching backlinks...',
  '3': 'Searching for affiliates...',
  '4': 'Classifying websites...',
  '5': 'Processing backlink data...',
  '6': 'Analyzing organic keywords...',
  '7': 'Analyzing paid keywords...',
  '8': 'Finalizing results...',
  'completed': 'Analysis complete!',
  'error': 'Processing failed. Please try again.',
  'failed': 'Processing failed. Please try again.'
};

// Async thunk for fetching all competitor brands
export const fetchAllComp = createAsyncThunk(
  'competitor/fetchAllComp',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_URL}/api/competitiveNew/fetchAllCompBrand`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.brands || [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Updated findComp thunk with proper comp limit handling
export const findComp = createAsyncThunk(
  'competitor/findComp',
  async (competitorData, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_URL}/api/competitiveNew/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(competitorData),
      });

      const result = await response.json();

      // Handle comp limit exceeded specifically
      if (response.status === 403 && result.error === 'COMP_LIMIT_EXCEEDED') {
        return rejectWithValue({
          error: 'COMP_LIMIT_EXCEEDED',
          message: result.message,
          data: result.data // Contains currentCompCount, allowedComps, userPlan, suggestedPlan
        });
      }

      if (!response.ok) {
        // Handle specific status codes
        if (response.status === 409 && result.brandExistsForUser) {
          // Brand already exists error
          return rejectWithValue({
            error: 'BRAND_EXISTS',
            message: result.message || 'Brand already exists for you'
          });
        }
        // Other errors
        return rejectWithValue({
          error: result.error || 'UNKNOWN_ERROR',
          message: result.error || result.message || `HTTP error! status: ${response.status}`
        });
      }

      return result;
    } catch (error) {
      return rejectWithValue({
        error: 'NETWORK_ERROR',
        message: error.message || 'Network error occurred. Please try again.'
      });
    }
  }
);

// Poll competitor brand processing status
export const pollCompBrandStatus = createAsyncThunk(
  'competitor/pollCompBrandStatus',
  async ({ brandId, brandUrl }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_URL}/api/competitiveNew/status/${brandId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        brandId,
        brandUrl,
        processingStatus: data.processingStatus || data.processing_status,
        isCompleted: data.isCompleted || data.processingStatus === 'completed',
        brandName: data.brandName,
        liveAffiliatesCount: data.liveAffiliatesCount || 0,
        organicKeywords: data.organicKeywords || 0,
        organicTraffic: data.organicTraffic || 0,
        totalTraffic: data.totalTraffic || 0,
        marsScore: data.marsScore || 0
      };

    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch processing status');
    }
  }
);

// Async thunk for checking brand processing status (used by CompCard)
export const checkBrandStatus = createAsyncThunk(
  'competitor/checkBrandStatus',
  async (brandId, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_URL}/api/competitiveNew/status/${brandId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  brands: [],
  isLoadingBrands: false,
  isAnalyzing: false,
  error: null,
  routeError: null,
  successMessage: null,
  formData: {
    brandName: '',
    spyUrl: ''
  },
  // Processing status tracking (same pattern as gapBrandsSlice)
  processingBrands: {}, // { brandUrl: { status: '1', step: 'Starting analysis...', brandName: 'Example', isActive: true, brandId: 123 } }
  isPolling: false,
  pollingIntervals: {}
};

const competitorSlice = createSlice({
  name: 'competitor',
  initialState,
  reducers: {
    // Form data management
    updateFormData: (state, action) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    resetFormData: (state) => {
      state.formData = { brandName: '', spyUrl: '' };
    },

    // Error and success message management
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.successMessage = null;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },

    // Route error management
    setRouteError: (state, action) => {
      state.routeError = action.payload;
    },
    clearRouteError: (state) => {
      state.routeError = null;
    },

    // Brand management
    updateBrandStatus: (state, action) => {
      const { brandId, status } = action.payload;
      const brandIndex = state.brands.findIndex(brand => brand.id === brandId);
      if (brandIndex !== -1) {
        state.brands[brandIndex].processing_status = status;
      }
    },
    addBrand: (state, action) => {
      state.brands.unshift(action.payload);
    },
    updateBrand: (state, action) => {
      const { id, ...updates } = action.payload;
      const brandIndex = state.brands.findIndex(brand => brand.id === id);
      if (brandIndex !== -1) {
        state.brands[brandIndex] = { ...state.brands[brandIndex], ...updates };
      }
    },
    removeBrand: (state, action) => {
      state.brands = state.brands.filter(brand => brand.id !== action.payload);
    },

    // ========== Processing Status Tracking (same pattern as gapBrandsSlice) ==========

    // Start polling for a competitor brand
    startCompPolling: (state, action) => {
      const { brandUrl, brandName, brandId } = action.payload;
      state.processingBrands[brandUrl] = {
        status: '1',
        step: PROCESSING_STEPS['1'],
        isActive: true,
        brandName: brandName || brandUrl,
        brandId: brandId,
        startTime: new Date().toISOString(),
        progress: 0 // 0-100 percentage
      };
      state.isPolling = true;
    },

    // Stop polling for a competitor brand
    stopCompPolling: (state, action) => {
      const { brandUrl } = action.payload;
      if (state.processingBrands[brandUrl]) {
        state.processingBrands[brandUrl].isActive = false;
        state.processingBrands[brandUrl].endTime = new Date().toISOString();
      }
      // Check if any brands are still being polled
      const hasActivePolling = Object.values(state.processingBrands).some(brand => brand.isActive);
      state.isPolling = hasActivePolling;
    },

    // Update processing step message
    updateCompProcessingStep: (state, action) => {
      const { brandUrl, step, status, progress } = action.payload;
      if (state.processingBrands[brandUrl]) {
        if (step) state.processingBrands[brandUrl].step = step;
        if (status) state.processingBrands[brandUrl].status = status;
        if (progress !== undefined) state.processingBrands[brandUrl].progress = progress;
      }
    },

    // Set polling interval ID for cleanup
    setCompPollingInterval: (state, action) => {
      const { brandUrl, intervalId } = action.payload;
      state.pollingIntervals[brandUrl] = intervalId;
    },

    // Clear polling interval
    clearCompPollingInterval: (state, action) => {
      const { brandUrl } = action.payload;
      if (state.pollingIntervals[brandUrl]) {
        delete state.pollingIntervals[brandUrl];
      }
    },

    // Clean up completed processing brands (older than 24h)
    cleanupCompProcessingBrands: (state) => {
      const cutoffTime = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      Object.keys(state.processingBrands).forEach(brandUrl => {
        const brand = state.processingBrands[brandUrl];
        if (!brand.isActive && brand.endTime && brand.endTime < cutoffTime) {
          delete state.processingBrands[brandUrl];
        }
      });
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all competitors
      .addCase(fetchAllComp.pending, (state) => {
        state.isLoadingBrands = true;
        state.error = null;
      })
      .addCase(fetchAllComp.fulfilled, (state, action) => {
        state.isLoadingBrands = false;
        state.brands = action.payload;
      })
      .addCase(fetchAllComp.rejected, (state, action) => {
        state.isLoadingBrands = false;
        state.error = action.payload;
      })
      
      // Find/Analyze competitor - Updated with comp limit handling
      .addCase(findComp.pending, (state) => {
        state.isAnalyzing = true;
        state.error = null;
        state.routeError = null;
      })
      .addCase(findComp.fulfilled, (state, action) => {
        state.isAnalyzing = false;
        
        if (action.payload.success) {
          state.successMessage = action.payload.message || 'Competitor analysis started successfully! Processing will begin shortly.';
        } else {
          state.successMessage = 'Analysis request submitted successfully!';
        }
        
        // Reset form data after successful submission
        state.formData = { brandName: '', spyUrl: '' };
      })
      .addCase(findComp.rejected, (state, action) => {
        state.isAnalyzing = false;
        
        // Handle comp limit exceeded separately (don't set as general error)
        if (action.payload?.error === 'COMP_LIMIT_EXCEEDED') {
          // Don't set this as a general error since we'll handle it with the special component
          state.error = null;
          state.routeError = null;
          // The component will handle this through the rejected action payload
        } else if (action.payload?.error === 'BRAND_EXISTS') {
          // Handle brand exists error
          state.routeError = action.payload.message;
        } else {
          // Handle other errors normally
          state.routeError = action.payload?.message || 'Failed to start competitor analysis';
        }
      })
      
      // Check brand status (this is primarily used by individual CompCard components)
      .addCase(checkBrandStatus.fulfilled, (state, action) => {
        // Update brand status in state if needed
        if (action.payload.success && action.payload.brandId) {
          const brandIndex = state.brands.findIndex(brand => brand.id === action.payload.brandId);
          if (brandIndex !== -1) {
            state.brands[brandIndex].processing_status = action.payload.processingStatus;
          }
        }
      })
      .addCase(checkBrandStatus.rejected, (state, action) => {
        // Handle polling errors silently since CompCard handles this
        console.error('Brand status check failed:', action.payload);
      })

      // ========== Poll Comp Brand Status ==========
      .addCase(pollCompBrandStatus.fulfilled, (state, action) => {
        const { brandUrl, processingStatus, isCompleted, brandId } = action.payload;

        if (state.processingBrands[brandUrl]) {
          state.processingBrands[brandUrl].status = processingStatus;
          state.processingBrands[brandUrl].step = PROCESSING_STEPS[processingStatus] || `Processing step ${processingStatus}...`;

          // Calculate progress percentage (8 total steps then completed)
          const statusNum = parseInt(processingStatus);
          if (!isNaN(statusNum)) {
            state.processingBrands[brandUrl].progress = Math.min(Math.round((statusNum / 8) * 100), 100);
          } else if (processingStatus === 'completed') {
            state.processingBrands[brandUrl].progress = 100;
          }

          // Stop polling if completed or failed (status 8 is the last numbered step, then 'completed')
          if (isCompleted || processingStatus === 'completed' || processingStatus === 'error' || processingStatus === 'failed') {
            state.processingBrands[brandUrl].isActive = false;
            state.processingBrands[brandUrl].endTime = new Date().toISOString();
            state.processingBrands[brandUrl].progress = 100;

            // Check if any brands are still being polled
            const hasActivePolling = Object.values(state.processingBrands).some(brand => brand.isActive);
            state.isPolling = hasActivePolling;
          }
        }

        // Also update the brand in the brands array
        const brandIndex = state.brands.findIndex(brand => brand.id === brandId || brand.brand_url === brandUrl);
        if (brandIndex !== -1) {
          state.brands[brandIndex].processing_status = processingStatus;
          if (action.payload.liveAffiliatesCount) {
            state.brands[brandIndex].live_affiliates_count = action.payload.liveAffiliatesCount;
          }
          if (action.payload.organicKeywords) {
            state.brands[brandIndex].organic_keywords = action.payload.organicKeywords;
          }
          if (action.payload.totalTraffic) {
            state.brands[brandIndex].total_traffic = action.payload.totalTraffic;
          }
          if (action.payload.marsScore) {
            state.brands[brandIndex].mars_score = action.payload.marsScore;
          }
        }
      })
      .addCase(pollCompBrandStatus.rejected, (state, action) => {
        console.error('Comp polling error:', action.payload);
      });
  }
});

export const {
  updateFormData,
  resetFormData,
  clearError,
  clearSuccess,
  setError,
  setRouteError,
  clearRouteError,
  updateBrandStatus,
  addBrand,
  updateBrand,
  removeBrand,
  // Processing status tracking actions
  startCompPolling,
  stopCompPolling,
  updateCompProcessingStep,
  setCompPollingInterval,
  clearCompPollingInterval,
  cleanupCompProcessingBrands
} = competitorSlice.actions;

// ========== Thunks for managing polling lifecycle ==========

// Start polling with automatic cleanup
export const startCompPollingWithCleanup = (brandData) => (dispatch, getState) => {
  const { brandUrl, brandName, brandId } = brandData;

  // Start polling in state
  dispatch(startCompPolling({ brandUrl, brandName, brandId }));

  // Set up polling interval (every 5 seconds)
  const intervalId = setInterval(() => {
    const state = getState();
    const processingInfo = state.competitor?.processingBrands[brandUrl];

    // Only poll if still active
    if (processingInfo?.isActive) {
      dispatch(pollCompBrandStatus({ brandId, brandUrl }));
    } else {
      // Clean up interval if no longer active
      clearInterval(intervalId);
      dispatch(clearCompPollingInterval({ brandUrl }));
    }
  }, 5000); // Poll every 5 seconds

  // Store interval ID for cleanup
  dispatch(setCompPollingInterval({ brandUrl, intervalId }));

  // Do an immediate poll
  dispatch(pollCompBrandStatus({ brandId, brandUrl }));

  return intervalId;
};

// Stop polling with cleanup
export const stopCompPollingWithCleanup = (brandUrl) => (dispatch, getState) => {
  const state = getState();
  const intervalId = state.competitor?.pollingIntervals[brandUrl];

  // Clear the interval
  if (intervalId) {
    clearInterval(intervalId);
    dispatch(clearCompPollingInterval({ brandUrl }));
  }

  // Stop polling in state
  dispatch(stopCompPolling({ brandUrl }));
};

// ========== Selectors ==========
export const selectCompBrands = (state) => state.competitor?.brands || [];
export const selectCompBrandsLoading = (state) => state.competitor?.isLoadingBrands || false;
export const selectCompBrandsError = (state) => state.competitor?.error || null;
export const selectCompProcessingBrands = (state) => state.competitor?.processingBrands || {};
export const selectCompIsPolling = (state) => state.competitor?.isPolling || false;
export const selectCompFormData = (state) => state.competitor?.formData || { brandName: '', spyUrl: '' };
export const selectCompIsAnalyzing = (state) => state.competitor?.isAnalyzing || false;
export const selectCompSuccessMessage = (state) => state.competitor?.successMessage || null;
export const selectCompRouteError = (state) => state.competitor?.routeError || null;

// Selector for getting processing status of a specific competitor brand
export const selectCompBrandProcessingStatus = (brandUrl) => (state) => {
  return state.competitor?.processingBrands[brandUrl] || null;
};

export default competitorSlice.reducer;